import React from "react"
import PropTypes from "prop-types"
import { toggle, setAll } from "../../../redux/selection"
import { showSelectedOnly } from "../../../redux/settings"
import { setParam, fetch } from "../../../redux/query"
import store from "../../../redux"
import { connect } from "react-redux"
import "../Dashboard.less"
import { withRouter } from "../../../lib/withRouter"
import { formatMoney, getDiagnosisCodes, getQuickSightUrl, getViewPatientData, normalizeCharges } from "../../../lib"
import moment from "moment"
import RangePicker from "../RangePicker"

export class ApproveTab extends React.Component {
    static propTypes = {
        query: PropTypes.object,
        selection: PropTypes.object,
        location: PropTypes.object,
        settings: PropTypes.object,
        urlParams: PropTypes.object,
        dispatch: PropTypes.func
    };

    constructor(props) {
        super(props);
        const startOfMonth = moment().startOf("month");
        const endOfMonth = moment().endOf("month");

        this.state = {
            approveListResult: null,
            tableLoading: false,
            error: null,
            dates: { start: startOfMonth, end: endOfMonth },
            continuationTokens: [null],
            currentPageIndex: 0,
        };
    }

    componentDidMount() {
        const query = new URLSearchParams(this.props.location?.search);

        // CHECK REDIRECT TABS
        const _tab = query.get("_tab");
        if (["demographics", "conditions", "tags"].includes(_tab)) {
            query.delete("_resource");
            const clean = query.toString() ? `?${query.toString()}` : "";
            this.props.router.navigate(`/patient-list${clean}`);
            return;
        }

        // SET ACTIVE TAB
        this.fetchApprovedData();
    }
    componentDidUpdate(prevProps, prevState) {
        // DATE RANGE CHANGED → fetch ONLY for approved/unapproved
        const prevStart = prevState.dates.start?.valueOf();
        const prevEnd = prevState.dates.end?.valueOf();
        const currStart = this.state.dates.start?.valueOf();
        const currEnd = this.state.dates.end?.valueOf();

        if (prevStart === currStart && prevEnd === currEnd) return;

        console.log("Date range changed → fetching…");

        this.fetchApprovedData();
    }

    handlePageChange = (page) => {
        this.setState({ currentPage: page });
    };

    fetchApprovedData = async () => {
        try {
            this.setState({ tableLoading: true });

            const start = this.state.dates.start.format("YYYY-MM-DD");
            const end = this.state.dates.end.format("YYYY-MM-DD");

            const token = this.state.continuationTokens[this.state.currentPageIndex];

            let apiUrl = `${this.props.settings.server.baseUrl}/claims/files?startDate=${start}&endDate=${end}&folderName=Processed&continuationToken=`;

            if (token) {
                apiUrl += `${btoa(token)}`;
            }

            const data = await getViewPatientData({
                url: apiUrl,
                server: this.props.settings.server
            });

            // New continuation token from server
            const records = data.records || [];
            const newToken = data.nextToken || null;

            this.setState(prev => {
                const updatedTokens = [...prev.continuationTokens];

                // Only push new token if it exists and not already in stack
                if (records.length > 0 && newToken && !updatedTokens.includes(newToken)) {
                    updatedTokens.push(newToken);
                }

                return {
                    approveListResult: records,
                    continuationTokens: updatedTokens,
                    tableLoading: false
                };
            });

        } catch (error) {
            this.setState({ error: error.message, tableLoading: false });
        }
    };

    handleNextPage = () => {
        const { currentPageIndex, continuationTokens } = this.state;

        const nextToken = continuationTokens[currentPageIndex + 1];
        if (!nextToken) return;

        this.setState(
            { currentPageIndex: currentPageIndex + 1 },
            this.fetchApprovedData
        );
    };

    handlePrevPage = () => {
        if (this.state.currentPageIndex === 0) return;

        this.setState(
            { currentPageIndex: this.state.currentPageIndex - 1 },
            this.fetchApprovedData
        );
    };


    renderTableRows = () => {
        const { approveListResult } = this.state;

        if (!approveListResult || approveListResult.length === 0)
            return (
                <tr><td colSpan="12" className="text-center">No records found</td></tr>
            );

        return approveListResult.map((claim, index) => {
            const data = claim.jsonData;
            const patient = data?.Patient || {};
            const charges = normalizeCharges(data.Charge);
            const payer = patient?.Policy?.Payer;

            return charges.map((charge, idx) => {
                const units = Number(charge?.Units) || 0;
                const unitPrice = Number(charge?.UnitPrice) || 0;
                const totalPrice = Number(charge?.TotalPrice);

                const totalAmount = totalPrice > 0
                    ? totalPrice
                    : unitPrice * units;

                const effectiveUnitPrice = unitPrice || (totalPrice && units ? totalPrice / units : 0);

                return (
                    <tr key={`${patient.Identifier}-${index}`}>
                        <td className="text-center">{patient.Identifier || "-"}</td>

                        <td>{`${patient.FirstName || ""} ${patient.LastName || ""}`.trim()}</td>

                        <td className="text-center">{patient.Gender || "-"}</td>
                        <td className="text-center">{payer?.Name || "-"}</td>

                        <td className="text-center">{charge?.FromDate || "-"}</td>
                        <td className="text-center">{charge?.ToDate || "-"}</td>

                        <td className="text-center">{charge?.CPTCode || "-"}</td>

                        <td className="text-center">{getDiagnosisCodes(charge)}</td>

                        <td className="text-center">{units || "-"}</td>

                        <td className="text-right">{formatMoney(effectiveUnitPrice) || "-"}</td>

                        <td className="text-right">{formatMoney(totalAmount) || "-"}</td>
                    </tr>
                );
            });
        });
    };

    handleDateRangeChange = (start, end) => {
        this.setState({
            dates: {
                start: moment(start).startOf("day"),
                end: moment(end).endOf("day")
            }
        });
    };

    render() {
        console.log("ApproveTab render", this.state.continuationTokens);
        const { error, approveListResult, tableLoading } = this.state;

        const hasPrev = this.state.currentPageIndex > 0;
        const hasNext = !!this.state.continuationTokens[this.state.currentPageIndex + 1];
        const currentPage = this.state.currentPageIndex + 1;


        if (error) {
            return <p className="text-danger">Error: {error}</p>;
        }

        const searchHeader = (
            <div className="panel-default">
                <div className="panel-heading" style={{ padding: "5px 15px" }}>
                    <div className="row d-flex align-items-center">
                        <div className="col-xs-4">
                            <b className="text-primary">
                                <i className="fa fa-address-card-o" /> Approved
                            </b>
                        </div>
                        <div className="col-xs-8">
                            <div className="row">
                                <div className="col-md-6 pull-right text-right">
                                    <RangePicker onRangeChange={this.handleDateRangeChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

        return (
            <>
                <div className="tab-content-wrapper" style={{ marginBottom: "2.5rem" }}>
                    {searchHeader}
                    <div className="panel-default">

                        <div className="table-responsive">
                            <table className="table table-condensed table-hover table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th className="text-center">Patient ID</th>
                                        <th>Patient Name</th>
                                        <th className="text-center">Gender</th>
                                        <th className="text-center">Payer</th>
                                        <th className="text-center">From Date</th>
                                        <th className="text-center">To Date</th>
                                        <th className="text-center">CPT Code</th>
                                        <th className="text-center">ICD Code</th>
                                        <th className="text-center">Units</th>
                                        <th className="text-center">Unit Price ($)</th>
                                        <th className="text-center">Total Amount ($)</th>
                                    </tr>
                                </thead>

                                <tbody className="table-body-loading">
                                    {this.state.tableLoading ? (
                                        <tr>
                                            <td colSpan="12" className="text-center">
                                                <i className="fa fa-spinner spin" style={{ fontSize: "22px" }} />
                                            </td>
                                        </tr>
                                    ) : (
                                        this.renderTableRows()
                                    )}

                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="app-footer"
                    style={{
                        position: "fixed",
                        bottom: 0,
                        width: "100%",
                        backgroundColor: "#f5f5f5",
                        borderTop: "1px solid #ddd",
                    }}
                >
                    <div className="container-fluid" style={{ width: "100%" }}>
                        <div className="row">

                            {/* Prev Button */}
                            <div className="col-xs-3 col-sm-4 text-right">
                                <a
                                    href="#prev"
                                    onClick={e => {
                                        e.preventDefault();
                                        if (hasPrev) this.handlePrevPage();
                                    }}
                                    style={{
                                        color: hasPrev ? '#007bff' : '#6c757d',
                                        cursor: hasPrev ? 'pointer' : 'not-allowed',
                                        textDecoration: 'none'
                                    }}
                                >
                                    <i className="fa fa-arrow-left" /> Prev
                                </a>
                            </div>

                            {/* Current Page Number */}
                            <div className="col-xs-6 col-sm-4 text-center">
                                <span className="pagination-status">
                                    Page {currentPage}
                                </span>
                            </div>

                            {/* Next Button */}
                            <div className="col-xs-3 col-sm-4 text-left">
                                <a
                                    href="#next"
                                    onClick={e => {
                                        e.preventDefault();
                                        if (hasNext) this.handleNextPage();
                                    }}
                                    style={{
                                        color: hasNext ? '#007bff' : '#6c757d',
                                        cursor: hasNext ? 'pointer' : 'not-allowed',
                                        textDecoration: 'none'
                                    }}
                                >
                                    Next <i className="fa fa-arrow-right" />
                                </a>
                            </div>

                        </div>
                    </div>
                </div>

            </>
        );
    }

}

export default connect(state => state)(withRouter(ApproveTab));
